import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../components/Button";
import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusTag } from "../../../../components/PlusTag";
import { Box, Container as BarContainer, StickySpacer } from "../../../../components/SaveBar/styles";
import { SubHeader } from "../../../../components/SubHeader";
import { SiteContextProvider } from "../../../../contexts/Site";
import { usePlusBenefits } from "../../../../hooks/PlusBenefits";
import { Site } from "../../../../model/site";
import { keepoInternalAnalyticsService } from "../../../../services/analytics";
import { api, ApiSiteResponse, hasActiveSubscription, SubscriptionDetail } from "../../../../services/api";
import {
  CancellationStep,
  CancellationSurvey,
  CancelReason,
  cancellationSurveyService,
  RetentionOffer,
} from "../../../../services/cancellationSurvey";
import { customDomainService } from "../../../../services/customDomain";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import { formatMoney } from "../../../../utils/money";
import { uuidv4 } from "../../../../utils/uuid";
import { CANCEL_REASONS, FOLLOW_UPS, RETENTION_OFFERS, shuffleReasons } from "./reasons";
import {
  Container,
  Field,
  Losses,
  Note,
  OfferCard,
  Option,
  Options,
  PlusButton,
  Scale,
  ScaleValue,
  SecondaryButton,
  Step,
} from "./styles";

const prefix = "pages.subscriptionCancel";

// Keep in sync with the backend's `Site::REFUNDING_DAYS_INTERVAL`: cancelling within this many days of
// subscribing is immediate and refunded (the Keepo goes back to FREE right away)
const REFUND_WINDOW_DAYS = 16;

// Keep in sync with the Stripe retention coupon (KEEPO_RETENTION_20: 20% off, once)
const RETENTION_DISCOUNT_PERCENT = 20;

type FlowStep = CancellationStep;

type FlowProps = {
  siteId: string;
  detail: SubscriptionDetail;
  sites: ApiSiteResponse[];
  hasCustomDomain: boolean;
};

/**
 * Asks why the user is leaving before cancelling: the main reason, a follow-up
 * on it, how much Plus helped, a retention offer that fits the reason and what they lose. Every step
 * is saved.
 */
const CancelFlow = ({ siteId, detail, sites, hasCustomDomain }: FlowProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const plusBenefits = usePlusBenefits();
  const detailUrl = `/subscription/detail?site=${siteId}`;

  const [surveyId] = useState(uuidv4);
  const [reasons] = useState(() => shuffleReasons(CANCEL_REASONS));
  const [step, setStep] = useState<FlowStep>("reason");

  const [reason, setReason] = useState<CancelReason>();
  const [reasonText, setReasonText] = useState("");
  const [detailOptions, setDetailOptions] = useState<string[]>([]);
  const [detailText, setDetailText] = useState("");
  const [value, setValue] = useState<number>();
  const [valueComment, setValueComment] = useState("");

  const [isDiscountEligible, setDiscountEligible] = useState(false);
  const [discountState, setDiscountState] = useState<"idle" | "applying" | "error">("idle");
  const [cancelState, setCancelState] = useState<"idle" | "canceling" | "error">("idle");
  // A PLUS paid once, within the right of withdrawal: the Pix was refunded, or the boleto's refund is support's
  const [refundOutcome, setRefundOutcome] = useState<"pix_refunded" | "boleto_support">();
  // Whether the offer step was shown (the offers then go with every later save)
  const [sawOffers, setSawOffers] = useState(false);

  useEffect(() => {
    cancellationSurveyService
      .getDiscountEligibility(siteId)
      .then(setDiscountEligible)
      .catch(() => setDiscountEligible(false));
  }, [siteId]);

  // When the subscription started: from Stripe, or else its oldest invoice (whatever order they come in)
  const startedAt =
    detail.startedAt ||
    [...(detail.invoices || [])].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf())[0]
      ?.createdAt;
  const isRefundWindow = !!startedAt && dayjs().diff(dayjs(startedAt), "day") < REFUND_WINDOW_DAYS;

  const unusedBenefits = useMemo(() => plusBenefits.tracked.filter(({ inUse }) => !inUse), [plusBenefits]);
  const followUp = reason ? FOLLOW_UPS[reason] : undefined;
  const offers = useMemo<RetentionOffer[]>(
    () =>
      (reason ? RETENTION_OFFERS[reason] : []).filter(
        // The backend decides the discount (within the refund window it applies to the next renewal)
        offer => (offer !== "discount" || isDiscountEligible) && (offer !== "benefits" || unusedBenefits.length > 0)
      ),
    [reason, isDiscountEligible, unusedBenefits]
  );

  // The steps depend on the reason: no follow-up or no offer for it means one step less
  const steps = useMemo<FlowStep[]>(
    () => [
      "reason",
      ...(!reason || followUp ? (["detail"] as const) : []),
      "value",
      ...(!reason || offers.length ? (["offer"] as const) : []),
      "confirm",
    ],
    [reason, followUp, offers]
  );

  const save = useCallback(
    (reached: CancellationStep, offerAccepted?: RetentionOffer) => {
      const survey: CancellationSurvey = {
        surveyId,
        siteId,
        step: reached,
        reason,
        reasonText: reason === "other" ? reasonText.trim() || undefined : undefined,
        detail: followUp
          ? { options: detailOptions.length ? detailOptions : undefined, text: detailText.trim() || undefined }
          : undefined,
        value,
        valueComment: valueComment.trim() || undefined,
        offersShown: reached === "offer" || sawOffers ? offers : undefined,
        offerAccepted,
        context: {
          benefitsUsed: plusBenefits.used,
          benefitsTotal: plusBenefits.total,
          unusedBenefits: unusedBenefits.map(({ key }) => key),
          hasCustomDomain,
          keeposCount: sites.filter(({ site }) => !!site).length,
          plusKeeposCount: sites.filter(({ subscription }) => hasActiveSubscription(subscription)).length,
          locale: i18n.language,
        },
      };
      cancellationSurveyService.saveProgress(survey).catch(() => undefined);
    },
    [
      surveyId,
      siteId,
      reason,
      reasonText,
      followUp,
      detailOptions,
      detailText,
      value,
      valueComment,
      sawOffers,
      offers,
      plusBenefits,
      unusedBenefits,
      hasCustomDomain,
      sites,
      i18n.language,
    ]
  );

  // Starting the flow is data too (how many start and don't cancel)
  useEffect(() => {
    save("reason");
  }, []);

  const goTo = (next: FlowStep) => {
    if (next === "offer") {
      setSawOffers(true);
    }
    save(next);
    setStep(next);
    window.scrollTo({ top: 0 });
  };
  const index = steps.indexOf(step);
  const goNext = () => goTo(steps[index + 1]);
  const goBack = () => (index > 0 ? setStep(steps[index - 1]) : navigate(detailUrl));

  const retain = (offer?: RetentionOffer) => save("retained", offer);

  const acceptDiscount = async () => {
    setDiscountState("applying");
    try {
      await cancellationSurveyService.applyDiscount(siteId);
      retain("discount");
      setStep("retained");
    } catch {
      setDiscountState("error");
    }
  };

  const cancelSubscription = async () => {
    setCancelState("canceling");
    try {
      const result = await cancellationSurveyService.cancelSubscription(siteId);
      setRefundOutcome(result?.refund || undefined);
      api
        .getUser()
        .then(user => keepoInternalAnalyticsService.notifySubscriptionCanceledEvent(user, siteId))
        .catch(() => undefined);
      save("canceled");
      setStep("canceled");
    } catch {
      setCancelState("error");
    }
  };

  const toggleDetailOption = (option: string, multiple: boolean) => {
    if (!multiple) {
      setDetailOptions([option]);
      return;
    }
    // "None of them" (in the benefits list) excludes the other options
    setDetailOptions(current =>
      option === "none"
        ? ["none"]
        : current.includes(option)
          ? current.filter(item => item !== option)
          : [...current.filter(item => item !== "none"), option]
    );
  };

  const canContinue =
    step === "reason"
      ? !!reason && (reason !== "other" || !!reasonText.trim())
      : step === "detail"
        ? detailOptions.length > 0
        : step === "value"
          ? value !== undefined
          : true;

  // What the next renewal costs with and without the discount (only once: the price goes back to normal after)
  const discountAmounts = detail.plan
    ? {
        original: formatMoney(detail.plan.amount, detail.plan.currency, i18n.language),
        discounted: formatMoney(
          Math.round((detail.plan.amount * (100 - RETENTION_DISCOUNT_PERCENT)) / 100),
          detail.plan.currency,
          i18n.language
        ),
      }
    : undefined;

  const periodEnd = formatLocaleDate(detail.currentPeriodEnd, i18n.language);

  const renderDetail = () => {
    if (!reason || !followUp) {
      return null;
    }
    const multiple = followUp.kind !== "single";
    const options =
      followUp.kind === "benefits"
        ? [
            ...[...plusBenefits.tracked, ...plusBenefits.included].map(({ key, title }) => ({ key, label: title })),
            { key: "none", label: t(`${prefix}.detail.lowUsage.none`) },
          ]
        : followUp.options.map(key => ({ key, label: t(`${prefix}.detail.${reason}.options.${key}`) }));
    const textLabelKey =
      reason === "competitor" ? `${prefix}.detail.competitor.textLabel` : `${prefix}.detail.textLabel`;

    return (
      <>
        <h3>{t(`${prefix}.detail.${reason}.title`)}</h3>
        <Options role={multiple ? "group" : "radiogroup"}>
          {options.map(({ key, label }) => (
            <Option
              key={key}
              type="button"
              role={multiple ? "checkbox" : "radio"}
              aria-checked={detailOptions.includes(key)}
              $selected={detailOptions.includes(key)}
              $multiple={multiple}
              onClick={() => toggleDetailOption(key, multiple)}>
              <span className="mark" />
              {label}
            </Option>
          ))}
        </Options>
        {followUp.withText && (
          <Field>
            {t(textLabelKey)}
            <textarea
              value={detailText}
              maxLength={1000}
              placeholder={t(`${prefix}.detail.textPlaceholder`)}
              onChange={event => setDetailText(event.target.value)}
            />
          </Field>
        )}
      </>
    );
  };

  if (step === "canceled") {
    return (
      <Step>
        <OfferCard>
          <h4>
            {refundOutcome === "boleto_support" ? t(`${prefix}.done.boletoSupportTitle`) : t(`${prefix}.done.title`)}
          </h4>
          <p>
            {refundOutcome === "pix_refunded"
              ? t(`${prefix}.done.pixRefunded`)
              : refundOutcome === "boleto_support"
                ? t(`${prefix}.done.boletoSupport`)
                : isRefundWindow
                  ? t(`${prefix}.done.refundDescription`)
                  : t(`${prefix}.done.description`, { date: periodEnd })}
          </p>
          {refundOutcome === "boleto_support" ? (
            <SecondaryButton
              type="button"
              onClick={() =>
                navigate("/help/chat", {
                  state: { back: { label: t("pages.subscriptionDetail.title"), to: detailUrl } },
                })
              }>
              {t(`${prefix}.done.talkToSupport`)}
            </SecondaryButton>
          ) : (
            <SecondaryButton type="button" onClick={() => navigate(refundOutcome ? "/sites" : detailUrl)}>
              {t(`${prefix}.done.cta`)}
            </SecondaryButton>
          )}
        </OfferCard>
      </Step>
    );
  }

  if (step === "retained") {
    return (
      <Step>
        <OfferCard $plus>
          <PlusTag>✦ Keepo Plus</PlusTag>
          <h4>
            {discountAmounts
              ? t(`${prefix}.offer.discount.success`, { date: periodEnd, ...discountAmounts })
              : t(`${prefix}.offer.discount.successNoAmount`, { percent: RETENTION_DISCOUNT_PERCENT })}
          </h4>
          <SecondaryButton type="button" onClick={() => navigate(detailUrl)}>
            {t(`${prefix}.back`)}
          </SecondaryButton>
        </OfferCard>
      </Step>
    );
  }

  return (
    <>
      <Step>
        <div className="progress" aria-live="polite">
          {t(`${prefix}.progress`, { current: index + 1, total: steps.length })}
          <div className="bar" aria-hidden="true">
            <span style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
          </div>
        </div>

        {step === "reason" && (
          <>
            <h3>{t(`${prefix}.reason.title`)}</h3>
            <p className="description">{t(`${prefix}.reason.description`)}</p>
            <Options role="radiogroup">
              {reasons.map(key => (
                <Option
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={reason === key}
                  $selected={reason === key}
                  onClick={() => {
                    setReason(key);
                    setDetailOptions([]);
                  }}>
                  <span className="mark" />
                  {t(`${prefix}.reason.options.${key}`)}
                </Option>
              ))}
            </Options>
            {reason === "other" && (
              <Field>
                <textarea
                  value={reasonText}
                  maxLength={1000}
                  placeholder={t(`${prefix}.reason.otherPlaceholder`)}
                  onChange={event => setReasonText(event.target.value)}
                />
              </Field>
            )}
          </>
        )}

        {step === "detail" && renderDetail()}

        {step === "value" && (
          <>
            <h3>{t(`${prefix}.value.title`)}</h3>
            <Scale>
              <div className="values" role="radiogroup">
                {[1, 2, 3, 4, 5].map(score => (
                  <ScaleValue
                    key={score}
                    type="button"
                    role="radio"
                    aria-checked={value === score}
                    $selected={value === score}
                    onClick={() => setValue(score)}>
                    {score}
                  </ScaleValue>
                ))}
              </div>
              <div className="labels">
                <span>{t(`${prefix}.value.low`)}</span>
                <span>{t(`${prefix}.value.high`)}</span>
              </div>
            </Scale>
            <Field>
              {t(`${prefix}.value.commentLabel`)}
              <textarea
                value={valueComment}
                maxLength={1000}
                placeholder={t(`${prefix}.value.commentPlaceholder`)}
                onChange={event => setValueComment(event.target.value)}
              />
            </Field>
          </>
        )}

        {step === "offer" && (
          <>
            <h3>{t(`${prefix}.offer.title`)}</h3>
            {offers.includes("discount") && (
              <OfferCard $plus>
                <PlusTag>✦ Keepo Plus</PlusTag>
                <h4>{t(`${prefix}.offer.discount.title`, { percent: RETENTION_DISCOUNT_PERCENT })}</h4>
                <p>
                  {discountAmounts
                    ? t(`${prefix}.offer.discount.description`, { date: periodEnd, ...discountAmounts })
                    : t(`${prefix}.offer.discount.descriptionNoAmount`)}
                </p>
                {discountState === "error" && <p className="error">{t(`${prefix}.offer.discount.error`)}</p>}
                <PlusButton type="button" disabled={discountState === "applying"} onClick={acceptDiscount}>
                  {discountState === "applying"
                    ? t(`${prefix}.offer.discount.applying`)
                    : t(`${prefix}.offer.discount.cta`)}
                </PlusButton>
              </OfferCard>
            )}
            {offers.includes("chat") && (
              <OfferCard>
                <h4>{t(`${prefix}.offer.chat.title`)}</h4>
                <p>{t(`${prefix}.offer.chat.description`)}</p>
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    retain("chat");
                    navigate("/help/chat");
                  }}>
                  {t(`${prefix}.offer.chat.cta`)}
                </SecondaryButton>
              </OfferCard>
            )}
            {offers.includes("benefits") && (
              <OfferCard>
                <h4>{t(`${prefix}.offer.benefits.title`)}</h4>
                <p>{t(`${prefix}.offer.benefits.description`)}</p>
                <ul>
                  {unusedBenefits.slice(0, 5).map(benefit => (
                    <li key={benefit.key}>
                      <button
                        type="button"
                        onClick={() => {
                          retain("benefits");
                          navigate(benefit.to);
                        }}>
                        {benefit.title}
                        <span>{t("pages.plusBenefits.use")} →</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </OfferCard>
            )}
          </>
        )}

        {step === "confirm" && (
          <>
            <h3>{t(`${prefix}.confirm.title`)}</h3>
            <p className="description">
              {isRefundWindow
                ? t(`${prefix}.confirm.refundDescription`, { days: REFUND_WINDOW_DAYS })
                : t(`${prefix}.confirm.description`, { date: periodEnd })}
            </p>
            <Losses>
              {hasCustomDomain && <li>{t(`${prefix}.confirm.losses.domain`)}</li>}
              <li>{t(`${prefix}.confirm.losses.brand`)}</li>
              <li>{t(`${prefix}.confirm.losses.sections`)}</li>
              <li>{t(`${prefix}.confirm.losses.chat`)}</li>
            </Losses>
            {cancelState === "error" && <Note className="error">{t(`${prefix}.confirm.error`)}</Note>}
          </>
        )}
      </Step>

      {/* Same fixed bar as the editor's save bar */}
      <StickySpacer />
      <Box $sticky>
        <BarContainer>
          {step === "confirm" ? (
            <>
              <LinkButton
                onClick={() => {
                  retain();
                  navigate(detailUrl);
                }}>
                {t(`${prefix}.confirm.keep`)}
              </LinkButton>
              <Button
                type="DANGER"
                size="LG"
                disabled={cancelState === "canceling"}
                onClick={() => cancelState !== "canceling" && cancelSubscription()}>
                {cancelState === "canceling" ? t(`${prefix}.confirm.canceling`) : t(`${prefix}.confirm.cta`)}
              </Button>
            </>
          ) : (
            <>
              <LinkButton onClick={goBack}>{t(`${prefix}.back`)}</LinkButton>
              <Button type="INFO" size="LG" disabled={!canContinue} onClick={() => canContinue && goNext()}>
                {step === "offer" ? t(`${prefix}.offer.decline`) : t(`${prefix}.next`)}
              </Button>
            </>
          )}
        </BarContainer>
      </Box>
    </>
  );
};

/** `/subscription/cancel?site=…`: loads the subscription and the Keepo, then runs the flow. */
export const SubscriptionCancelPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const siteId = new URLSearchParams(location.search).get("site");

  const [data, setData] = useState<{
    detail: SubscriptionDetail;
    sites: ApiSiteResponse[];
    hasCustomDomain: boolean;
  } | null>();

  useEffect(() => {
    if (!siteId) {
      return;
    }
    Promise.all([
      api.getSubscriptionDetail(siteId),
      api.getSites(),
      customDomainService.get(siteId).catch(() => undefined),
    ])
      .then(([detail, sites, domain]) =>
        setData(Object.keys(detail).length ? { detail, sites, hasCustomDomain: domain?.status === "connected" } : null)
      )
      .catch(() => setData(null));
  }, [siteId]);

  if (!siteId || data === null) {
    return <Navigate to={siteId ? `/subscription/detail?site=${siteId}` : "/sites"} replace />;
  }
  if (!data) {
    return <LoadingModal />;
  }

  const editingSite = data.sites.find(site => site.siteId === siteId);
  const site = (editingSite?.site || editingSite?.draft) as Site | undefined;
  if (!site) {
    return <Navigate to="/sites" replace />;
  }

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.header.title`)}
        crumbs={[{ label: t("pages.subscriptionDetail.title"), to: `/subscription/detail?site=${siteId}` }]}
      />
      {/* Subscription screens live outside a Keepo's screens: give the flow its Keepo (for its benefits) */}
      <SiteContextProvider value={{ site, setSite: () => undefined }}>
        <CancelFlow siteId={siteId} detail={data.detail} sites={data.sites} hasCustomDomain={data.hasCustomDomain} />
      </SiteContextProvider>
    </Container>
  );
};
